import type { CandidateStatus } from '@eduvy/core';
import type { Channel, InterviewStatus, Recommendation, Verdict } from './api';

type Tone = 'slate' | 'blue' | 'amber' | 'green' | 'red' | 'violet' | 'indigo';

export const CANDIDATE_STATUS: Record<CandidateStatus, { label: string; tone: Tone }> = {
  NEW: { label: 'New', tone: 'slate' },
  QUEUED: { label: 'Queued', tone: 'blue' },
  INVITED: { label: 'Link sent', tone: 'blue' },
  CALLING: { label: 'Calling', tone: 'blue' },
  IN_INTERVIEW: { label: 'In interview', tone: 'violet' },
  INTERVIEWED: { label: 'Scoring', tone: 'violet' },
  QUALIFIED: { label: 'Qualified', tone: 'green' },
  NOT_QUALIFIED: { label: 'Not qualified', tone: 'red' },
  NEEDS_REVIEW: { label: 'Needs review', tone: 'amber' },
  UNREACHABLE: { label: 'Unreachable', tone: 'slate' },
  SHORTLISTED: { label: 'Shortlisted', tone: 'indigo' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  ON_HOLD: { label: 'On hold', tone: 'amber' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'slate' },
  ASSESSMENT_INVITED: { label: 'Assessment sent', tone: 'blue' },
  IN_ASSESSMENT: { label: 'In assessment', tone: 'violet' },
  ASSESSED: { label: 'Assessed', tone: 'indigo' },
  MEETING_INVITED: { label: 'Recruiter interview', tone: 'blue' },
  SELECTED: { label: 'Selected', tone: 'green' },
};

export const INTERVIEW_STATUS: Record<InterviewStatus, { label: string; tone: Tone }> = {
  PENDING: { label: 'Pending', tone: 'slate' },
  INVITED: { label: 'Invited', tone: 'blue' },
  RINGING: { label: 'Ringing', tone: 'blue' },
  IN_PROGRESS: { label: 'In progress', tone: 'violet' },
  COMPLETED: { label: 'Completed', tone: 'green' },
  NO_ANSWER: { label: 'No answer', tone: 'amber' },
  BUSY: { label: 'Busy', tone: 'amber' },
  FAILED: { label: 'Failed', tone: 'red' },
  ABANDONED: { label: 'Dropped', tone: 'amber' },
  DECLINED: { label: 'Declined', tone: 'slate' },
  EXPIRED: { label: 'Expired', tone: 'slate' },
  CANCELLED: { label: 'Cancelled', tone: 'slate' },
};

export const VERDICT: Record<Verdict, { label: string; tone: Tone }> = {
  QUALIFIED: { label: 'Qualified', tone: 'green' },
  NEEDS_REVIEW: { label: 'Needs review', tone: 'amber' },
  NOT_QUALIFIED: { label: 'Not qualified', tone: 'red' },
};

export const RECOMMENDATION: Record<Recommendation, string> = {
  STRONG_YES: 'Strong yes',
  YES: 'Yes',
  MAYBE: 'Maybe',
  NO: 'No',
};

/** Badge tones on the design tokens (light and dark alike). */
export const TONE_CLASSES: Record<Tone, string> = {
  slate: 'bg-muted text-muted-fg ring-border-strong',
  blue: 'bg-info-soft text-info ring-info/25',
  amber: 'bg-warning-soft text-warning ring-warning/25',
  green: 'bg-success-soft text-success ring-success/25',
  red: 'bg-destructive-soft text-destructive ring-destructive/25',
  violet: 'bg-purple/10 text-purple ring-purple/25',
  indigo: 'bg-accent-soft text-accent ring-accent/25',
};

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function formatPhone(e164: string | null): string {
  if (!e164) return '—';
  const m = e164.match(/^\+91(\d{5})(\d{5})$/);
  return m ? `+91 ${m[1]} ${m[2]}` : e164;
}

export function whatsappLink(e164: string, message: string): string {
  return `https://wa.me/${e164.replace(/^\+/, '')}?text=${encodeURIComponent(message)}`;
}

export function scoreColour(score: number): string {
  if (score >= 70) return 'bg-success';
  if (score >= 55) return 'bg-warning';
  return 'bg-destructive';
}

/** Text colour for a score, by the same bands as the bars. */
export function scoreText(score: number): string {
  if (score >= 70) return 'text-success';
  if (score >= 55) return 'text-warning';
  return 'text-destructive';
}

/** Paise -> "₹5,500" (whole rupees) or "₹5,500.50". */
export function formatMoney(paise: number): string {
  const rupees = paise / 100;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: paise % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(rupees);
}

/** "October 2026" for a billing month. */
export function monthName(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function titleCase(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const CHANNEL_LABEL: Record<Channel, string> = {
  WEB: 'Web voice',
  VIDEO: 'Video interview',
  PHONE: 'Phone call',
};

export const OUTREACH_LABEL: Record<string, string> = {
  WEB_LINK: 'Web link by email',
  PHONE_CALL: 'Phone call',
  PHONE_THEN_WEB: 'Phone call, then web link',
};

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!.charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '';
  return (first + last).toUpperCase();
}
